// orgSettingsService.js
//
// Build 25.1: organization-wide settings, including the two-tier app
// access model.
//
// Data model (intentionally minimal — future multi-tenant builds will
// generalize this to a collection):
//
//   system/orgSettings document:
//     orgName: string                    e.g. "Wixon & Cross"
//     enabledAppIds: string[]            org-wide enabled suite apps
//     createdAt: serverTimestamp
//     updatedAt: serverTimestamp
//
//   orgSettingsAudit/{autoId} collection (append-only audit log):
//     at: serverTimestamp
//     userId, userEmail
//     field: 'enabledAppIds' | 'orgName'
//     before, after  (the value before and after)
//     confirmedWith: string              what the admin typed to confirm
//
// Effective access for a given user + appId:
//   org_enables(appId) AND user_has(appId)
//
// The admin role no longer short-circuits app access — admins are subject
// to their own assignedSuiteApps and the org's enabledAppIds. /admin
// itself is always reachable for admins (safeguard against lockout).

import {
  doc, getDoc, setDoc, updateDoc,
  collection, addDoc, query, orderBy, limit, getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAllActiveAppIds } from '../constants/appCatalog';

const ORG_DOC_PATH = 'system/orgSettings';
const AUDIT_COLLECTION = 'orgSettingsAudit';

/** Default org settings used when the doc doesn't yet exist. */
function defaultOrgSettings() {
  return {
    orgName: 'Wixon & Cross',
    enabledAppIds: getAllActiveAppIds()
  };
}

/**
 * Read the current org settings doc.
 *
 * If the doc doesn't exist, returns the default. Doesn't create it — that's
 * what ensureOrgSettings is for. Read this from listeners and access checks
 * where you don't want a side effect.
 */
export async function getOrgSettings() {
  try {
    const snap = await getDoc(doc(db, ORG_DOC_PATH));
    if (snap.exists()) {
      return { id: snap.id, ...defaultOrgSettings(), ...snap.data() };
    }
    return { id: 'orgSettings', ...defaultOrgSettings() };
  } catch (err) {
    console.warn('Failed to read org settings, falling back to defaults:', err);
    return { id: 'orgSettings', ...defaultOrgSettings() };
  }
}

/**
 * Ensure the org settings doc exists. Idempotent — does nothing if it's
 * already there. Called once on admin sign-in so new orgs get a sensible
 * starting state (all apps enabled, default name).
 *
 * Only admins should call this. Read rules allow anyone to fetch settings,
 * but write rules require admin.
 */
export async function ensureOrgSettings() {
  try {
    const snap = await getDoc(doc(db, ORG_DOC_PATH));
    if (snap.exists()) return { created: false };
    await setDoc(doc(db, ORG_DOC_PATH), {
      ...defaultOrgSettings(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { created: true };
  } catch (err) {
    console.error('ensureOrgSettings failed:', err);
    return { created: false, error: err };
  }
}

/**
 * Update a field on org settings AND write an audit entry in one go.
 *
 * @param field          'enabledAppIds' or 'orgName'
 * @param newValue       the new value
 * @param oldValue       the value being replaced (for the audit log)
 * @param user           the current Firebase user
 * @param confirmedWith  the literal string the admin typed to confirm
 *
 * Both writes happen in sequence (not a transaction — Firestore doesn't
 * support cross-collection transactions from the web SDK without admin
 * SDK). If the audit write fails, the change still applies. We log the
 * failure but don't roll back, because losing one audit entry is less
 * bad than rolling back a settings change the admin already committed
 * to. The settings change going through without an audit entry is a
 * detectable anomaly (settings doc updatedAt > most recent audit entry).
 */
export async function updateOrgSetting({ field, newValue, oldValue, user, confirmedWith }) {
  // 1. Write the change to the settings doc
  await updateDoc(doc(db, ORG_DOC_PATH), {
    [field]: newValue,
    updatedAt: serverTimestamp(),
    updatedBy: user.email
  });

  // 2. Write an audit entry. Failure here is logged but not fatal.
  try {
    await addDoc(collection(db, AUDIT_COLLECTION), {
      at: serverTimestamp(),
      userId: user.uid,
      userEmail: user.email,
      field,
      before: oldValue,
      after: newValue,
      confirmedWith: confirmedWith || ''
    });
  } catch (auditErr) {
    console.error('Audit log write failed (settings change still applied):', auditErr);
  }
}

/**
 * Fetch the most recent audit entries.
 *
 * @param max  how many to return (default 5)
 */
export async function getRecentAudit(max = 5) {
  try {
    const q = query(
      collection(db, AUDIT_COLLECTION),
      orderBy('at', 'desc'),
      limit(max)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Audit log fetch failed:', err);
    return [];
  }
}

/**
 * Fetch the full audit history. Used by the dedicated /admin/org-settings/audit
 * page. Capped at 500 entries to avoid runaway reads; for production we'd
 * want pagination but it's overkill here.
 */
export async function getFullAudit(max = 500) {
  return getRecentAudit(max);
}
